export const regexEmail = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/

// ============================================
// Tipos de sociedad disponibles
// ============================================
export const TIPOS_SOCIEDAD = [
  { codigo: "SA", descripcion: "Sociedad Anónima (SA)" },
  { codigo: "SRL", descripcion: "Sociedad de Responsabilidad Limitada (SRL)" },
  { codigo: "SAS", descripcion: "Sociedad por Acciones Simplificada (SAS)" },
  { codigo: "SH", descripcion: "Sociedad de Hecho (SH)" },
  { codigo: "SC", descripcion: "Sociedad Colectiva (SC)" },
  { codigo: "SCA", descripcion: "Sociedad en Comandita por Acciones (SCA)" },
  { codigo: "SCS", descripcion: "Sociedad en Comandita Simple (SCS)" },
  { codigo: "SCOOP", descripcion: "Sociedad Cooperativa" },
  { codigo: "FIDEICOMISO", descripcion: "Fideicomiso" },
  { codigo: "CONSORCIO", descripcion: "Consorcio" },
  { codigo: "UTE", descripcion: "Unión Transitoria de Empresas (UTE)" },
  { codigo: "FUNDACION", descripcion: "Fundación" },
  { codigo: "ASOCIACION", descripcion: "Asociación Civil" },
  { codigo: "UNIPERSONAL", descripcion: "Empresa Unipersonal" },
  { codigo: "PF", descripcion: "Persona Física" },
  { codigo: "OTRO", descripcion: "Otro tipo" },
]

// ============================================
// Función para detectar tipo de sociedad desde CUIT
// ============================================
/**
 * Intenta detectar el tipo de sociedad basándose en el CUIT.
 * Los primeros 2 dígitos del CUIT indican el tipo de contribuyente:
 * - 20, 23, 24, 27: Persona Física
 * - 30: Sociedad (SA, SRL, etc.)
 * - 33: Sociedad (generalmente SA o entidades sin fines de lucro)
 * - 34: Sociedad (generalmente SRL o pequeñas empresas)
 *
 * Nota: El CUIT no permite distinguir entre SA, SRL, SAS, etc.
 * Solo podemos saber si es persona física o jurídica.
 *
 * @param {string} cuit - CUIT a analizar
 * @returns {Object} { tipoDetectado, confianza, esPersonaJuridica, mensaje }
 */
export const detectarTipoSociedadDesdeCuit = cuit => {
  if (!cuit) {
    return {
      tipoDetectado: null,
      confianza: "NINGUNA",
      esPersonaJuridica: null,
      mensaje: "No se proporcionó CUIT",
    }
  }

  const cuitLimpio = cuit.replace(/[^0-9]/g, "")

  if (cuitLimpio.length !== 11) {
    return {
      tipoDetectado: null,
      confianza: "NINGUNA",
      esPersonaJuridica: null,
      mensaje: "CUIT inválido (debe tener 11 dígitos)",
    }
  }

  const prefijo = cuitLimpio.substring(0, 2)

  // Prefijos de persona física
  const prefijosPF = ["20", "23", "24", "27"]

  // Prefijos de persona jurídica
  const prefijosPJ = ["30", "33", "34"]

  if (prefijosPF.includes(prefijo)) {
    return {
      tipoDetectado: "PF",
      confianza: "ALTA",
      esPersonaJuridica: false,
      mensaje: "CUIT corresponde a Persona Física",
      requiereConfirmacion: false,
    }
  }

  if (prefijosPJ.includes(prefijo)) {
    // Es persona jurídica, pero no podemos saber el tipo exacto
    // Sugerimos SA como default porque es el más común
    return {
      tipoDetectado: null, // No podemos determinar el tipo exacto
      confianza: "BAJA",
      esPersonaJuridica: true,
      mensaje:
        "CUIT corresponde a Persona Jurídica. Seleccione el tipo de sociedad.",
      requiereConfirmacion: true,
      sugerencia: "SA", // Sugerencia por defecto
    }
  }

  return {
    tipoDetectado: null,
    confianza: "NINGUNA",
    esPersonaJuridica: null,
    mensaje: "No se pudo determinar el tipo desde el CUIT",
    requiereConfirmacion: true,
  }
}

// ============================================
// Reglas de negocio por tipo de sociedad
// ============================================
/**
 * Define qué personas relacionadas se requieren según el tipo de sociedad
 */
export const REGLAS_TIPO_SOCIEDAD = {
  SA: {
    personasRequeridas: ["Presidente"],
    personasOpcionales: [
      "Vicepresidente",
      "Director",
      "Director Suplente",
      "Síndico",
    ],
    requiereBiometria: ["Presidente"],
    descripcionOrgano: "Directorio",
    cantidadMinimaFirmantes: 1,
  },
  SRL: {
    personasRequeridas: ["Socio Gerente"],
    personasOpcionales: ["Socio", "Gerente"],
    requiereBiometria: ["Socio Gerente"], // Todos los socios gerentes
    descripcionOrgano: "Gerencia",
    cantidadMinimaFirmantes: 1,
  },
  SAS: {
    personasRequeridas: ["Administrador"],
    personasOpcionales: ["Socio", "Representante Legal"],
    requiereBiometria: ["Administrador"],
    descripcionOrgano: "Administración",
    cantidadMinimaFirmantes: 1,
  },
  SH: {
    personasRequeridas: ["Socio"],
    personasOpcionales: [],
    requiereBiometria: ["Socio"], // Todos los socios
    descripcionOrgano: "Socios",
    cantidadMinimaFirmantes: 1, // Al menos un socio
    notaEspecial:
      "Todos los socios son solidaria e ilimitadamente responsables",
  },
  SC: {
    personasRequeridas: ["Socio"],
    personasOpcionales: ["Administrador"],
    requiereBiometria: ["Socio"],
    descripcionOrgano: "Socios",
    cantidadMinimaFirmantes: 1,
  },
  SCA: {
    personasRequeridas: ["Socio Comanditado", "Socio Comanditario"],
    personasOpcionales: ["Director"],
    requiereBiometria: ["Socio Comanditado"],
    descripcionOrgano: "Socios Comanditados",
    cantidadMinimaFirmantes: 1,
  },
  PF: {
    personasRequeridas: ["Titular"],
    personasOpcionales: ["Apoderado"],
    requiereBiometria: ["Titular"],
    descripcionOrgano: "Titular",
    cantidadMinimaFirmantes: 1,
  },
  FIDEICOMISO: {
    personasRequeridas: ["Fiduciario"],
    personasOpcionales: ["Fiduciante", "Beneficiario"],
    requiereBiometria: ["Fiduciario"],
    descripcionOrgano: "Fiduciario",
    cantidadMinimaFirmantes: 1,
  },
  // Default para otros tipos
  OTRO: {
    personasRequeridas: ["Representante Legal"],
    personasOpcionales: ["Apoderado"],
    requiereBiometria: ["Representante Legal"],
    descripcionOrgano: "Representación",
    cantidadMinimaFirmantes: 1,
  },
}

/**
 * Obtiene las reglas para un tipo de sociedad
 */
export const getReglasTipoSociedad = tipoSociedad => {
  return REGLAS_TIPO_SOCIEDAD[tipoSociedad] || REGLAS_TIPO_SOCIEDAD.OTRO
}

// ============================================
// Campos del formulario de Titular
// ============================================

export const TITULAR_LEFT = [
  {
    label: "CUIT",
    value: "cuit",
    type: "text",
    isRequired: true,
    isSearchable: true,
  },
  {
    label: "Razón social",
    value: "razonSocial",
    type: "text",
    isRequired: true,
  },
  // NUEVO: Tipo de sociedad
  {
    label: "Tipo de Sociedad",
    value: "tipoSociedad",
    type: "select",
    refValue: "tiposSociedad",
    isRequired: true,
    helpText: "Se detecta automáticamente del CUIT cuando es posible",
  },
  {
    label: "Domicilio Fiscal",
    value: "domicilioFiscal",
    type: "text",
    isRequired: true,
  },
  {
    label: "Provincia",
    value: "provincia",
    type: "select",
    refValue: "provincia",
    isRequired: true,
  },
  {
    label: "Localidad",
    value: "localidad",
    type: "text",
    isRequired: false,
  },
  {
    label: "Asesor comercial",
    value: "asesorComercial",
    type: "text",
    refValue: "asesorComercial",
    isRequired: false,
  },
]

export const TITULAR_RIGHT = [
  {
    label: "Sector Económico",
    value: "sectorEconomico",
    type: "select",
    refValue: "sectorEconomico",
    isRequired: false,
    hasReference: true,
    showCodeDsc: true,
  },
  {
    label: "Código actividad",
    value: "actividadCodigo",
    type: "select",
    refValue: "actividad",
    optionValueToShow: "codigo",
    isRequired: true,
  },
  {
    label: "Descripción actividad",
    value: "actividad",
    type: "text",
    readOnly: true,
    refValue: "actividad",
    isRequired: false,
  },
  {
    label: "Situación BCRA",
    value: "situacionBCRAID",
    refValue: "situacionBCRA",
    type: "select",
    isRequired: true,
    hasReference: true,
    showCodeDsc: true,
  },
  {
    label: "Score Vigente",
    value: "scoreVigente",
    type: "text",
    readOnly: true,
  },
]

export const MESES = [
  { descripcion: "ENERO" },
  { descripcion: "FEBRERO" },
  { descripcion: "MARZO" },
  { descripcion: "ABRIL" },
  { descripcion: "MAYO" },
  { descripcion: "JUNIO" },
  { descripcion: "JULIO" },
  { descripcion: "AGOSTO" },
  { descripcion: "SEPTIEMBRE" },
  { descripcion: "OCTUBRE" },
  { descripcion: "NOVIEMBRE" },
  { descripcion: "DICIEMBRE" },
]

export const TITULAR_FIELDS = [...TITULAR_LEFT, ...TITULAR_RIGHT]
